import { useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import {
  ArrowUpRightIcon,
  BoltIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardDocumentCheckIcon,
  CubeIcon,
  ExclamationTriangleIcon,
  FireIcon,
  MagnifyingGlassIcon,
  Squares2X2Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { endpoints } from '../../services/cfoApi'
import { Leyenda } from '../common/leyendas'
import DetalleReorden, { LEADS } from './DetalleReorden'
import { ordenar } from './comun'
import { FILTRO_VACIO, NIVELES_PRODUCTO } from '../ventas/FiltroProducto'

const fmtNum = (n) => Number(n || 0).toLocaleString('es-GT')

// Pestaña "Reposición" de Compras. Mira hacia adelante desde hoy: no depende del
// filtro de fechas (usa el consumo de los últimos N meses).
// Lead time propio por línea ({ "<línea>": días }): se recuerda en este navegador.
const LS_LEAD_LINEAS = 'abaco_reposicion_lead_lineas'
const leerLeadLineas = () => {
  try { return JSON.parse(localStorage.getItem(LS_LEAD_LINEAS) || '{}') || {} } catch { return {} }
}

// producto / proveedor = filtros de arriba de la página de Compras
export default function Reposicion({ producto = FILTRO_VACIO, setProducto = () => {}, proveedor = null }) {
  const [params, setParams] = useState({ horizonte_meses: 3, meses_consumo: 6, lead_time: 45 })
  const [leadLineas, setLeadLineasState] = useState(leerLeadLineas)
  const setLeadLinea = (linea, dias) => setLeadLineasState(prev => {
    const next = { ...prev }
    if (dias === null) delete next[linea]
    else next[linea] = dias
    try { localStorage.setItem(LS_LEAD_LINEAS, JSON.stringify(next)) } catch { /* sin almacenamiento */ }
    return next
  })
  const leadLineasJson = JSON.stringify(leadLineas)
  const filtros = {
    ...Object.fromEntries(NIVELES_PRODUCTO.filter(n => producto[n.dim]).map(n => [n.dim, producto[n.dim]])),
    ...(proveedor ? { proveedor: proveedor.codigo } : {}),
  }
  const { data, isLoading, isFetching } = useQuery(
    ['compras-recomendaciones', params.horizonte_meses, params.meses_consumo, params.lead_time, leadLineasJson, JSON.stringify(filtros)],
    () => endpoints.compras.recomendaciones({ ...params, ...filtros, ...(Object.keys(leadLineas).length ? { lead_lineas: leadLineasJson } : {}) }),
    { keepPreviousData: true, staleTime: 60_000 }
  )
  return (
    <RecomendacionesSection
      reco={data?.data || {}} loading={isLoading} actualizando={isFetching}
      params={params} setParams={setParams} leadLineas={leadLineas} setLeadLinea={setLeadLinea}
      producto={producto} setProducto={setProducto}
    />
  )
}

// Selector de lead time de una línea: "General" = usa el lead time general
function LeadLineaSelect({ linea, leadLineas, setLeadLinea, general, className = '' }) {
  const propio = leadLineas[linea]
  return (
    <select
      value={propio ?? ''}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => setLeadLinea(linea, e.target.value === '' ? null : Number(e.target.value))}
      className={`rounded border px-1.5 py-0.5 text-xs tabular-nums ${propio !== undefined ? 'border-[#001639] bg-[#001639] text-white' : 'border-[var(--border-default)] bg-[var(--bg-primary)] text-[var(--text-secondary)]'} ${className}`}
      title={`Lead time de ${linea}`}
    >
      <option value="" className="text-black">General ({general}d)</option>
      {LEADS.map(l => <option key={l} value={l} className="text-black">{l} días</option>)}
    </select>
  )
}

// Encabezado ordenable de la tabla de artículos
function ThOrden({ col, orden, setOrden, align = 'right', className = '', leyenda, children }) {
  const activo = orden.col === col
  const Icon = activo && orden.dir === 'asc' ? ChevronUpIcon : ChevronDownIcon
  return (
    <th className={`text-${align} font-semibold py-2 ${className}`}>
      <button
        onClick={() => setOrden(activo ? { col, dir: orden.dir === 'desc' ? 'asc' : 'desc' } : { col, dir: align === 'left' ? 'asc' : 'desc' })}
        className={`inline-flex items-center gap-0.5 uppercase hover:text-[var(--text-primary)] ${activo ? 'text-[var(--text-primary)]' : ''}`}
        title="Ordenar"
      >
        {children}
        <Icon className={`w-3 h-3 ${activo ? '' : 'opacity-30'}`} />
      </button>
      {leyenda && <Leyenda k={leyenda} className="ml-0.5" />}
    </th>
  )
}
const RANGO_PRIO = { urgente: 0, alta: 1, media: 2, ok: 3 }

// =========================================================================
// RECOMENDACIONES DE REPOSICIÓN
// =========================================================================
const PRIORIDAD = {
  urgente: { label: 'Urgente', bg: 'bg-rose-500/10',   text: 'text-rose-600',   ring: 'ring-rose-500/40',   dot: 'bg-rose-500'  },
  alta:    { label: 'Alta',    bg: 'bg-amber-500/10',  text: 'text-amber-600',  ring: 'ring-amber-500/40',  dot: 'bg-amber-500' },
  media:   { label: 'Media',   bg: 'bg-sky-500/10',    text: 'text-sky-600',    ring: 'ring-sky-500/40',    dot: 'bg-sky-500'   },
  ok:      { label: 'OK',      bg: 'bg-emerald-500/10',text: 'text-emerald-600',ring: 'ring-emerald-500/40',dot: 'bg-emerald-500'},
}

// Filtros de prioridad de la tabla. "Acción inmediata" (urgente + alta) es el default;
// al buscar o al elegir una línea se pasa a "Todas" para ver el universo completo.
const FILTROS_PRIORIDAD = [
  ['accion',  'Acción inmediata', it => it.prioridad === 'urgente' || it.prioridad === 'alta'],
  ['todas',   'Todas',            () => true],
  ['urgente', 'Urgente',          it => it.prioridad === 'urgente'],
  ['alta',    'Alta',             it => it.prioridad === 'alta'],
  ['media',   'Media',            it => it.prioridad === 'media'],
  ['ok',      'OK',               it => it.prioridad === 'ok'],
]

function RecomendacionesSection({ reco, loading, actualizando, params, setParams, leadLineas, setLeadLinea, producto, setProducto }) {
  const [tab, setTab] = useState('articulos') // 'articulos' | 'lineas'
  // La línea (subcategoría) se elige con el filtro de producto de arriba
  const filtroLinea = producto.subcategoria
  const [filtroPrio, setFiltroPrio] = useState('accion')
  const [busqueda, setBusqueda] = useState('')
  const [seleccion, setSeleccion] = useState(null)
  // Orden de la tabla; 'prio_rank' asc = prioridad y luego valor sugerido (orden del backend)
  const [orden, setOrden] = useState({ col: 'prio_rank', dir: 'asc' })
  const kpis = reco?.kpis || {}
  const lineas = reco?.lineas || []
  const items = reco?.items || reco?.urgentes || []

  const conteoPrio = useMemo(
    () => Object.fromEntries(FILTROS_PRIORIDAD.map(([id, , fn]) => [id, items.filter(fn).length])),
    [items]
  )

  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase()
    const fn = FILTROS_PRIORIDAD.find(([id]) => id === filtroPrio)?.[2] || (() => true)
    const base = items.filter(it =>
      fn(it) &&
      (!q || `${it.descripcion} ${it.codigo} ${it.proveedor_sugerido} ${it.sublinea}`.toLowerCase().includes(q))
    ).map((it, i) => ({ ...it, prio_rank: RANGO_PRIO[it.prioridad] * 1e6 + i, cobertura_orden: it.dias_cobertura ?? -1 }))
    return ordenar(base, orden)
  }, [items, filtroPrio, busqueda, orden])

  const buscar = (v) => {
    setBusqueda(v)
    if (v.trim() && filtroPrio === 'accion') setFiltroPrio('todas')
  }
  const limpiar = () => { setBusqueda(''); setFiltroPrio('accion') }
  const hayFiltros = busqueda || filtroPrio !== 'accion'

  if (loading && !reco?.kpis) {
    return (
      <div className="card p-8">
        <div className="animate-pulse space-y-3">
          <div className="h-6 bg-[var(--bg-secondary)] rounded w-1/3" />
          <div className="grid grid-cols-4 gap-3">
            {[1,2,3,4].map(i => <div key={i} className="h-20 bg-[var(--bg-secondary)] rounded" />)}
          </div>
          <div className="h-64 bg-[var(--bg-secondary)] rounded" />
        </div>
      </div>
    )
  }

  return (
    <div className="card overflow-hidden">
      {/* Header con gradient */}
      <div className="bg-gradient-to-r from-[#001639] via-[#001a45] to-[#003a7a] p-5 text-white">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2">
              <ClipboardDocumentCheckIcon className="w-5 h-5 text-amber-300" />
              <h2 className="text-lg font-bold text-white flex items-center gap-1.5">Recomendación de reposición <Leyenda k="reposicion" claro /></h2>
            </div>
            <p className="text-xs text-white/60 mt-1">
              Basado en consumo de {params.meses_consumo}m · lead time general {params.lead_time}d{Object.keys(leadLineas).length ? ` (${Object.keys(leadLineas).length} línea${Object.keys(leadLineas).length === 1 ? '' : 's'} con lead propio)` : ''} · horizonte {params.horizonte_meses}m · safety stock 30%
              {actualizando && <span className="ml-2 text-amber-300">recalculando…</span>}
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <label className="flex items-center gap-1.5">
              <span className="text-white/60">Horizonte</span>
              <select
                value={params.horizonte_meses}
                onChange={(e) => setParams(p => ({ ...p, horizonte_meses: +e.target.value }))}
                className="bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-xs"
              >
                <option value={1}>1 mes</option>
                <option value={2}>2 meses</option>
                <option value={3}>3 meses</option>
                <option value={6}>6 meses</option>
              </select>
            </label>
            <label className="flex items-center gap-1.5">
              <span className="text-white/60" title="Aplica a las líneas sin lead time propio">Lead general</span>
              <select
                value={params.lead_time}
                onChange={(e) => setParams(p => ({ ...p, lead_time: +e.target.value }))}
                className="bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-xs"
              >
                {LEADS.map(l => <option key={l} value={l} className="text-black">{l} días</option>)}
              </select>
            </label>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          <RecoKpi
            label="SKUs urgentes"
            leyenda="prioridad_reposicion"
            value={kpis.skus_urgentes ?? 0}
            sublabel={`de ${kpis.skus_activos || 0} activos`}
            icon={FireIcon}
            tone="rose"
          />
          <RecoKpi
            label="Alta prioridad"
            leyenda="prioridad_reposicion"
            value={kpis.skus_alta ?? 0}
            sublabel="< 1.5x lead time"
            icon={ExclamationTriangleIcon}
            tone="amber"
          />
          <RecoKpi
            label="Valor sugerido"
            leyenda="reposicion"
            value={fmtQm(kpis.valor_sugerido_total)}
            sublabel={`Cobertura prom ${kpis.cobertura_promedio_dias || 0}d`}
            icon={BoltIcon}
            tone="emerald"
            isValue
          />
          <RecoKpi
            label="Inventario actual"
            leyenda="reposicion"
            value={fmtQm(kpis.valor_inventario_total)}
            sublabel={`${kpis.skus_ok || 0} SKUs OK · ${kpis.skus_media || 0} media`}
            icon={CubeIcon}
            tone="sky"
            isValue
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-[var(--border-default)] flex items-center gap-1 px-4 bg-[var(--bg-secondary)]">
        <TabBtn active={tab === 'articulos'} onClick={() => setTab('articulos')} icon={FireIcon} count={kpis.skus_activos}>
          Artículos
        </TabBtn>
        <TabBtn active={tab === 'lineas'} onClick={() => setTab('lineas')} icon={Squares2X2Icon} count={lineas.length}>
          Por línea
        </TabBtn>
      </div>

      {/* Contenido de tab */}
      {tab === 'lineas' && (
        <div className="p-5 space-y-3">
          {lineas.length === 0 ? (
            <p className="text-sm text-[var(--text-muted)] py-8 text-center">Sin líneas con consumo en el período.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {lineas.map(l => (
                <LineaCard
                  key={l.linea}
                  linea={l}
                  leadSelect={<LeadLineaSelect linea={l.linea} leadLineas={leadLineas} setLeadLinea={setLeadLinea} general={params.lead_time} />}
                  onFocus={() => { setProducto({ ...FILTRO_VACIO, categoria: l.categoria, subcategoria: l.linea }); setBusqueda(''); setFiltroPrio('todas'); setTab('articulos') }}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'articulos' && (
        <div className="p-5">
          {/* Barra de filtros: búsqueda, línea y prioridad */}
          <div className="mb-4 flex items-center gap-2 flex-wrap">
            <div className="relative">
              <MagnifyingGlassIcon className="w-4 h-4 text-[var(--text-muted)] absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={busqueda}
                onChange={(e) => buscar(e.target.value)}
                onKeyDown={(e) => e.key === 'Escape' && setBusqueda('')}
                placeholder="Buscar artículo, código o proveedor…"
                className="input py-1.5 pl-8 text-sm w-72 max-w-full"
              />
            </div>
            {filtroLinea && (
              <label className="flex items-center gap-1.5 text-xs text-[var(--text-muted)]">
                Lead time de la línea
                <LeadLineaSelect linea={filtroLinea} leadLineas={leadLineas} setLeadLinea={setLeadLinea} general={params.lead_time} className="py-1" />
              </label>
            )}
            <div className="flex rounded-md border border-[var(--border-default)] overflow-hidden text-xs flex-wrap">
              {FILTROS_PRIORIDAD.map(([id, label]) => (
                <button
                  key={id}
                  onClick={() => setFiltroPrio(id)}
                  className={`px-2.5 py-1.5 font-medium flex items-center gap-1 ${filtroPrio === id ? 'bg-[#001639] text-white' : 'text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)]'}`}
                >
                  {PRIORIDAD[id] && <span className={`w-1.5 h-1.5 rounded-full ${PRIORIDAD[id].dot}`} />}
                  {label}
                  <span className={`tabular-nums ${filtroPrio === id ? 'text-white/70' : 'text-[var(--text-muted)]'}`}>{conteoPrio[id] ?? 0}</span>
                </button>
              ))}
            </div>
            {hayFiltros && (
              <button onClick={limpiar} className="text-xs text-[var(--accent-blue)] hover:underline flex items-center gap-0.5">
                <XMarkIcon className="w-3.5 h-3.5" /> limpiar
              </button>
            )}
            <span className="ml-auto text-xs text-[var(--text-muted)] tabular-nums">
              {fmtNum(filtrados.length)} artículo{filtrados.length === 1 ? '' : 's'} · sugerido {fmtQm(filtrados.reduce((s, it) => s + (it.valor_sugerido || 0), 0))}
            </span>
          </div>
          {filtrados.length === 0 ? (
            <p className="text-sm text-[var(--text-muted)] py-8 text-center">
              {busqueda ? `Ningún artículo coincide con "${busqueda}".` : 'Sin artículos en el filtro actual.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-[10px] text-[var(--text-muted)] uppercase border-b border-[var(--border-default)]">
                    <ThOrden col="prio_rank" orden={orden} setOrden={setOrden} align="left" leyenda="prioridad_reposicion">Prioridad</ThOrden>
                    <ThOrden col="descripcion" orden={orden} setOrden={setOrden} align="left">Artículo</ThOrden>
                    <ThOrden col="linea" orden={orden} setOrden={setOrden} align="left">Línea</ThOrden>
                    <ThOrden col="stock_actual" orden={orden} setOrden={setOrden}>Stock</ThOrden>
                    <ThOrden col="cobertura_orden" orden={orden} setOrden={setOrden} leyenda="reposicion">Cobertura</ThOrden>
                    <ThOrden col="lead_time_dias" orden={orden} setOrden={setOrden} leyenda="lead_time_reposicion">Lead</ThOrden>
                    <ThOrden col="consumo_mensual" orden={orden} setOrden={setOrden}>Consumo/mes</ThOrden>
                    <ThOrden col="cantidad_sugerida" orden={orden} setOrden={setOrden} leyenda="reposicion">Sugerido (uds)</ThOrden>
                    <ThOrden col="valor_sugerido" orden={orden} setOrden={setOrden}>Valor sugerido</ThOrden>
                    <ThOrden col="proveedor_sugerido" orden={orden} setOrden={setOrden} align="left" className="pl-3">Proveedor</ThOrden>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border-default)]">
                  {filtrados.map((u, i) => (
                    <SkuUrgenteRow key={u.codigo + i} item={u} leadPropio={leadLineas[u.linea] !== undefined} onClick={() => setSeleccion(u)} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {seleccion && <DetalleReorden item={seleccion} params={params} onClose={() => setSeleccion(null)} />}
    </div>
  )
}

function TabBtn({ active, onClick, icon: Icon, count, children }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
        active
          ? 'border-[#001639] text-[var(--text-primary)]'
          : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
      }`}
    >
      <Icon className="w-4 h-4" />
      {children}
      {count > 0 && (
        <span className={`text-xs px-1.5 py-0.5 rounded-full tabular-nums ${
          active ? 'bg-[#001639] text-white' : 'bg-[var(--bg-tertiary)] text-[var(--text-muted)]'
        }`}>
          {count}
        </span>
      )}
    </button>
  )
}

function RecoKpi({ label, value, sublabel, icon: Icon, tone, isValue, leyenda }) {
  const toneCls = tone === 'rose'    ? 'text-rose-300'
                : tone === 'amber'   ? 'text-amber-300'
                : tone === 'emerald' ? 'text-emerald-300'
                : tone === 'sky'     ? 'text-sky-300'
                :                       'text-white'
  return (
    <div className="bg-white/5 border border-white/10 rounded-lg p-3 backdrop-blur-sm">
      <div className="flex items-start justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wider text-white/60 font-semibold flex items-center gap-1">{label} {leyenda && <Leyenda k={leyenda} claro />}</span>
        <Icon className={`w-4 h-4 ${toneCls}`} />
      </div>
      <p className={`text-2xl font-bold tabular-nums leading-tight ${toneCls}`}>{isValue ? value : Number(value).toLocaleString('es-GT')}</p>
      <p className="text-[10px] text-white/50 mt-1">{sublabel}</p>
    </div>
  )
}

function LineaCard({ linea, onFocus, leadSelect }) {
  const total = linea.skus || 1
  const pctUrgente = (linea.skus_urgentes / total) * 100
  const pctAlta    = (linea.skus_alta    / total) * 100
  const pctMedia   = (linea.skus_media   / total) * 100
  const pctOk      = (linea.skus_ok      / total) * 100
  const priorityCls = linea.skus_urgentes > 0
    ? 'border-rose-500/40 bg-gradient-to-br from-rose-500/5 to-transparent'
    : linea.skus_alta > 0
      ? 'border-amber-500/40 bg-gradient-to-br from-amber-500/5 to-transparent'
      : 'border-[var(--border-default)]'

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onFocus}
      onKeyDown={(e) => e.key === 'Enter' && onFocus()}
      className={`text-left rounded-lg border ${priorityCls} p-4 hover:shadow-md transition-all bg-[var(--bg-primary)] card-hover cursor-pointer`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="min-w-0">
          <p className="text-sm font-bold truncate">{linea.linea}</p>
          <p className="text-[11px] text-[var(--text-muted)] truncate">{linea.proveedor_top || 'Sin proveedor principal'}</p>
        </div>
        {linea.skus_urgentes > 0 && (
          <span className="text-[10px] uppercase font-bold tracking-wider bg-rose-500/10 text-rose-600 px-1.5 py-0.5 rounded ring-1 ring-rose-500/30">
            {linea.skus_urgentes} urg
          </span>
        )}
      </div>

      {/* Stack bar por prioridad */}
      <div className="h-2 bg-[var(--bg-tertiary)] rounded-full overflow-hidden flex mb-2">
        {pctUrgente > 0 && <div className="bg-rose-500 h-full" style={{ width: `${pctUrgente}%` }} />}
        {pctAlta    > 0 && <div className="bg-amber-500 h-full" style={{ width: `${pctAlta}%` }} />}
        {pctMedia   > 0 && <div className="bg-sky-500 h-full" style={{ width: `${pctMedia}%` }} />}
        {pctOk      > 0 && <div className="bg-emerald-500 h-full" style={{ width: `${pctOk}%` }} />}
      </div>

      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
        <div>
          <p className="text-[var(--text-muted)]">SKUs</p>
          <p className="font-semibold tabular-nums">{linea.skus}</p>
        </div>
        <div>
          <p className="text-[var(--text-muted)]">Cobertura</p>
          <p className={`font-semibold tabular-nums ${linea.cobertura_dias < 30 ? 'text-rose-500' : linea.cobertura_dias < 60 ? 'text-amber-500' : 'text-emerald-500'}`}>
            {linea.cobertura_dias !== null ? `${linea.cobertura_dias}d` : '—'}
          </p>
        </div>
        <div>
          <p className="text-[var(--text-muted)]">Inventario</p>
          <p className="font-semibold tabular-nums">{fmtQm(linea.valor_inventario)}</p>
        </div>
        <div>
          <p className="text-[var(--text-muted)]">Sugerido</p>
          <p className="font-bold tabular-nums text-[var(--accent-blue)]">{fmtQm(linea.valor_sugerido_total)}</p>
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-[var(--border-default)] flex items-center justify-between gap-2 text-[10px] text-[var(--text-muted)]">
        <span className="flex items-center gap-1.5">Lead time {leadSelect}</span>
        <span className="flex items-center gap-0.5">Ver artículos <ArrowUpRightIcon className="w-3 h-3" /></span>
      </div>
    </div>
  )
}

function SkuUrgenteRow({ item, leadPropio, onClick }) {
  const p = PRIORIDAD[item.prioridad] || PRIORIDAD.media
  const stockCritico = item.dias_cobertura !== null && item.dias_cobertura < item.lead_time_dias
  return (
    <tr onClick={onClick} className="text-xs hover:bg-[var(--bg-secondary)] cursor-pointer" title="Ver punto de reorden">
      <td className="py-2 pr-2">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${p.bg} ${p.text} ring-1 ${p.ring}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${p.dot}`} />
          {p.label}
        </span>
      </td>
      <td className="py-2 pr-3 max-w-[280px]">
        <p className="font-semibold nombre-articulo text-[var(--text-primary)]">{item.descripcion}</p>
        <p className="text-[10px] text-[var(--text-muted)]">{item.codigo}</p>
      </td>
      <td className="py-2 pr-2 text-[var(--text-secondary)]">
        <p className="truncate max-w-[110px]">{item.linea}</p>
      </td>
      <td className="py-2 text-right tabular-nums">
        <p className={`font-semibold ${stockCritico ? 'text-rose-500' : ''}`}>{fmtNum(Math.round(item.stock_actual))}</p>
        {item.stock_transito > 0 && (
          <p className="text-[10px] text-[var(--text-muted)]">+{fmtNum(Math.round(item.stock_transito))} tránsito</p>
        )}
      </td>
      <td className="py-2 text-right tabular-nums">
        {item.dias_cobertura === null ? (
          <span className="text-rose-500 font-bold">0d</span>
        ) : (
          <span className={`font-bold ${item.dias_cobertura < item.lead_time_dias ? 'text-rose-500' : item.dias_cobertura < item.lead_time_dias * 1.5 ? 'text-amber-500' : 'text-[var(--text-primary)]'}`}>
            {item.dias_cobertura}d
          </span>
        )}
      </td>
      <td className="py-2 text-right tabular-nums">
        <span className={leadPropio ? 'font-semibold text-[var(--text-primary)]' : 'text-[var(--text-muted)]'}>{item.lead_time_dias}d</span>
        {leadPropio && <p className="text-[10px] text-[var(--text-muted)]">de la línea</p>}
      </td>
      <td className="py-2 text-right tabular-nums text-[var(--text-secondary)]">
        {fmtNum(Math.round(item.consumo_mensual))}
      </td>
      <td className="py-2 text-right tabular-nums font-semibold">
        {fmtNum(item.cantidad_sugerida)}
      </td>
      <td className="py-2 text-right tabular-nums font-bold text-[var(--accent-blue)]">
        {fmtQm(item.valor_sugerido)}
      </td>
      <td className="py-2 pl-3 max-w-[180px]">
        <p className="truncate text-[var(--text-secondary)]">{item.proveedor_sugerido}</p>
        {item.proveedor_dias_credito !== null && (
          <p className="text-[10px] text-[var(--text-muted)]">crédito {item.proveedor_dias_credito}d</p>
        )}
      </td>
    </tr>
  )
}

// Helper Q compact para recomendaciones
function fmtQm(n) {
  const v = Number(n) || 0
  if (Math.abs(v) >= 1e6) return `Q${(v / 1e6).toFixed(1)}M`
  if (Math.abs(v) >= 1e3) return `Q${Math.round(v / 1e3)}k`
  return `Q${Math.round(v).toLocaleString('es-GT')}`
}
